import { useQuery } from '@apollo/client';
import React from 'react';
import { compose, withState } from 'recompose';
import { withRouter } from 'react-router-dom';

import CollectionList, { DefaultTitle } from '@/se/components/collection/CollectionList';

import schema from '../../../graph/bracelets';
import columns from './columns';
import { isDefinedAndNotNull } from '@/se/utilities/check';
import { sortBy, sortNumberDesc } from '@/se/utilities/data/array';
import TitleAction from '@/se/components/TitleAction';
import RouterLink from '@/se/components/RouterLink';
import { withSession } from '@/state/Session';
import ConnectionProvider from '../../pages/kiosk/ConnectionProvider';
import { ArrowBack } from '@material-ui/icons';
import { UnauthorizedScreen } from '@/components/pages/kiosk/UnauthorizedScreen';
import { mapUnauthorizedError } from '@/essentials/mapUnauthorizedError';

// Stable constants to avoid re-creating props on each render
const EMPTY_OBJ = Object.freeze({});
const ID_PROVIDER = item => item.id;
const NO_TITLE_AND_ACTIONS = () => null;

// Memoized getList to avoid re-sorting when the source array reference is unchanged.
// Also sorts on a copy to avoid mutating Apollo cache data.
const getSortedBracelets = (() => {
  let lastArr = null;
  let lastSorted = null;
  return params => {
    const bracelets = params?.data?.bracelets;
    if (!isDefinedAndNotNull(bracelets)) return null;
    if (bracelets === lastArr) return lastSorted;
    lastArr = bracelets;
    lastSorted = [...bracelets].sort(sortBy('batteryLevel')(sortNumberDesc));
    return lastSorted;
  };
})();

const BraceletsList = React.memo(function BraceletsList() {
  const query = useQuery(schema.list);
  return (
    <CollectionList
      entityName={'Bracelets'}
      idProvider={ID_PROVIDER}
      getList={getSortedBracelets}
      TitleAndActions={NO_TITLE_AND_ACTIONS}
      columns={columns}
      data={query}
      location={EMPTY_OBJ}
      history={EMPTY_OBJ}
    />
  );
});

const Bracelets = ({ match, accessToken }) => {
  const [unauthorized, setUnauthorized] = React.useState(null);

  const handleUnauthorized = error => setUnauthorized(mapUnauthorizedError(error));

  if (unauthorized) {
    return <UnauthorizedScreen description={unauthorized} />;
  }

  return (
    <ConnectionProvider
      accessToken={accessToken}
      organizationId={match.params.organizationId}
      onUnauthorized={handleUnauthorized}
    >
      <div>
        <TitleAction
          Back={() => (
            <RouterLink to={'/issues'}>
              <ArrowBack fontSize={'small'} />
              &nbsp;Issues
            </RouterLink>
          )}
          Title={() => <DefaultTitle>Bracelets</DefaultTitle>}
        />

        <BraceletsList />
      </div>
    </ConnectionProvider>
  );
};

export default compose(
  withRouter,
  withState('isInFullScreen', 'setIsInFullScreen', false),
  withSession(({ session }) => ({ accessToken: session && session.token }))
)(Bracelets);
